import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Typography, Card, DatePicker, Button, Tag, Tooltip, Popconfirm, Modal, Input, message,
} from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from '../../services/api';

const { Title, Text } = Typography;

const fmtMoney = (v) => `£${Number(v || 0).toFixed(2)}`;

const ExternalDrivers = () => {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [payNotes, setPayNotes] = useState('');

  const monthStr = selectedMonth.format('YYYY-MM');

  const fetchDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.driverWages.getExternal(monthStr);
      setDrivers(res.data || []);
    } catch {
      message.error('Failed to load external drivers');
    } finally {
      setLoading(false);
    }
  }, [monthStr]);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  const payingRow = drivers.find(d => d._id === payingId);

  // The fee isn't tracked on this page, so the payment records status only
  const handleMarkPaid = async () => {
    try {
      await apiService.driverWages.markPaid({
        driver: payingRow._id,
        month: monthStr,
        daysWorked: payingRow.daysWorked,
        dailyRate: 0,
        amount: 0,
        notes: payNotes,
      });
      message.success(`${payingRow.name} marked as paid`);
      setPayingId(null);
      setPayNotes('');
      fetchDrivers();
    } catch {
      message.error('Failed to mark as paid');
    }
  };

  const handleUnmarkPaid = async (row) => {
    try {
      await apiService.driverWages.unmarkPaid(row._id, monthStr);
      message.success('Payment undone');
      fetchDrivers();
    } catch {
      message.error('Failed to undo payment');
    }
  };

  const sum = (field) => drivers.reduce((s, d) => s + (d[field] || 0), 0);

  const columns = [
    { title: 'Driver', dataIndex: 'name', key: 'name', render: (name) => <Text strong>{name}</Text> },
    { title: 'Days Worked', dataIndex: 'daysWorked', key: 'daysWorked', align: 'center', width: 110 },
    { title: 'Jobs', dataIndex: 'jobs', key: 'jobs', align: 'center', width: 80 },
    { title: 'Charged', dataIndex: 'charged', key: 'charged', align: 'right', width: 120, render: fmtMoney },
    {
      title: 'Status',
      key: 'status',
      align: 'center',
      width: 120,
      render: (_, record) =>
        record.paid ? (
          <Tooltip title={`Paid on ${dayjs(record.payment?.paidAt).format('DD MMM YYYY')}`}>
            <Tag icon={<CheckCircleOutlined />} color="success">Paid</Tag>
          </Tooltip>
        ) : (
          <Tag icon={<ClockCircleOutlined />} color="warning">Unpaid</Tag>
        ),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'right',
      width: 130,
      render: (_, record) =>
        record.paid ? (
          <Popconfirm
            title="Undo this payment?"
            onConfirm={() => handleUnmarkPaid(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button size="small" danger type="text">Undo</Button>
          </Popconfirm>
        ) : (
          <Button
            size="small"
            type="primary"
            onClick={() => { setPayingId(record._id); setPayNotes(''); }}
          >
            Mark Paid
          </Button>
        ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
        <Title level={4} style={{ margin: 0 }}>External Drivers</Title>
        <DatePicker
          picker="month"
          value={selectedMonth}
          onChange={(d) => d && setSelectedMonth(d)}
          allowClear={false}
          format="MMMM YYYY"
          style={{ width: 160 }}
        />
      </div>

      <Card size="small">
        <Table
          dataSource={drivers}
          columns={columns}
          rowKey="_id"
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No external drivers worked this month' }}
          onRow={(record) => ({
            onClick: (e) => {
              // Don't navigate if clicking the action buttons or their popover
              if (e.target.closest('button') || e.target.closest('.ant-popover')) return;
              navigate(`/daily-runs/external-drivers/${record._id}?name=${encodeURIComponent(record.name)}&month=${monthStr}`);
            },
            style: { cursor: 'pointer' },
          })}
          summary={() =>
            drivers.length > 0 ? (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}><Text strong>Total</Text></Table.Summary.Cell>
                <Table.Summary.Cell index={1} />
                <Table.Summary.Cell index={2} align="center"><Text strong>{sum('jobs')}</Text></Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right"><Text strong>{fmtMoney(sum('charged'))}</Text></Table.Summary.Cell>
                <Table.Summary.Cell index={4} colSpan={2} />
              </Table.Summary.Row>
            ) : null
          }
        />
      </Card>

      <Modal
        open={!!payingId}
        title={`Mark ${payingRow?.name} as paid for ${selectedMonth.format('MMMM YYYY')}`}
        onCancel={() => setPayingId(null)}
        onOk={handleMarkPaid}
        okText="Confirm Payment"
      >
        <Input.TextArea
          placeholder="Notes (optional)"
          value={payNotes}
          onChange={e => setPayNotes(e.target.value)}
          rows={2}
        />
      </Modal>
    </div>
  );
};

export default ExternalDrivers;
